import React, {useState} from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import {getCookie} from "../../tools/tools";
import {useTranslation} from "react-i18next";
import math from 'mathjs';
import IndexNavbar from "../../components/Navbars/IndexNavbar";
import {Container} from "reactstrap";
import Footer from "../../components/Footers/IndexFooter";
import {BlockMath} from 'react-katex';
import 'katex/dist/katex.min.css'

const f = (k, t, p_func, Ta, T0, dt, T1, T2) => {
    const p = p_func;
    const def1 = (T1 - Ta) / (T0 - Ta) - p / (p - k) * Math.exp(-k * t) + k / (p - k) * Math.exp(-p * t);
    const def2 = (T2 - Ta) / (T0 - Ta) - p / (p - k) * Math.exp(-k * (t + dt)) + k / (p - k) * Math.exp(-p * (t + dt));
    return 0.5 * (def1 ** 2 + def2 ** 2);
};

const get3dPlotData = (p_func, T1, T2, Ta, dt, T0) => {
    const t_max = 24;
    const k_max = 1;
    const t = Array.from({length: t_max * 2}, (_, i) => (i + 1) * 0.5);
    const k = Array.from({length: k_max * 100}, (_, i) => (i + 1) * 0.01);

    const z = k.map(ki => t.map(ti => f(ki, ti, p_func(ki, ti), Ta, T0, dt, T1, T2)));

    return {x: t, y: k, z: z};
};

const useTemperatureCalculator = () => {
    const brain = (k, Ta) => 8.425 * k;

    const rectum = (k, Ta) => (Ta < 23.2 ? 5 * k : 10 * k);

    const getFuncByDiagnosticPoint = (diagnosticPoint) => {
        return diagnosticPoint === 'Brain' ? brain : rectum;
    };

    return {getFuncByDiagnosticPoint};
};

const Zcalc1 = () => {
    const {t} = useTranslation();
    const {getFuncByDiagnosticPoint} = useTemperatureCalculator();
    const kMax = 1;
    const tMax = 24;
    const [data, setData] = useState(null);
    const [formData, setFormData] = useState({
        diagnostic_point: 'Rectum',
        T1: 25,
        dT1: 0.1,
        T2: 23.9,
        dT2: 0.1,
        Ta: 10,
        dTa: 0.1,
        dt: 1.0841722,
        ddt: 0.0055,
        T0: 37.2,
        dT0: 0.3,
    });

    const names = {
        'T1': 'T1measurement',
        'T2': 'T2measurement',
        'Ta': 'Ta',
        'dt': 'dt',
        'T0': 'T0'
    }

    const [result, setResult] = useState(null);
    const [errors, setErrors] = useState([]);

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData({...formData, [name]: value});
    };

    const handleSubmit = async () => {
        try {
            const response = await axios.post('/api/z-calc/1', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'X-CSRFToken': getCookie('csrftoken')
                },
                withCredentials: true,
            });
            if (response.data.errors && response.data.errors.length > 0) {
                setErrors(response.data.errors);
                setResult(null);
            } else {
                setResult(response.data);
                setData(get3dPlotData(getFuncByDiagnosticPoint(formData.diagnostic_point), formData.T1, formData.T2, formData.Ta, formData.dt, formData.T0));
                setErrors([]);
            }
        } catch (error) {
            console.error('Error occurred:', error);
            const errors = error.response.data.errors;
            if(errors.length > 0)
                setErrors(errors);
            else
                setErrors(['An error occurred while processing the request.']);
        }
    };

    const coolingData =
        result && result.cooling
            ? [
                {
                    x: result.cooling.solution.x,
                    y: result.cooling.solution.y,
                    name: t("CoolingCurve"),
                    mode: "lines",
                    line: {color: "blue"},
                },
                {
                    x: result.cooling.lower.x,
                    y: result.cooling.lower.y,
                    name: t("lower"),
                    mode: "lines",
                    line: {color: "red"},
                },
                {
                    x: result.cooling.upper.x,
                    y: result.cooling.upper.y,
                    name: t("upper"),
                    mode: "lines",
                    line: {color: "red"},
                },
            ]
            : [];

    const itersData =
        result && result.iters
            ? {
                x: result.iters.map((i) => i[0]),
                y: result.iters.map((i) => i[1]),
                z: result.iters.map((i) => 0.5 * (i[2] ** 2 + i[3] ** 2)),
                mode: "markers",
                marker: {
                    size: 5,
                    color: result.iters.map(
                        (i) => 0.5 * (i[2] ** 2 + i[3] ** 2)
                    ),
                    colorscale: "Viridis",
                },
            }
            : null;

    const points = result && result.iters ?
        result.iters.filter(([_t, k]) => _t >= 0 && _t <= tMax && k >= 0 && k <= kMax)
        : null;

    return (
        <div className="content">
            <IndexNavbar brandText={t("Simple FEM Calculator")}/>
            <div className="section section-components pt-100">
                <div className="container mb-5 pb-100">
                    <h1 className="h1 text-success font-weight-bold mb-4">Warm Bodies Z</h1>
                    <small className="mb-3 text-uppercase font-weight-bold">{t('inputDataParameters')}</small>

                    <div className="form-group row">
                        <label className="col-sm-4 col-form-label">{t('diagnosticPoint')}:</label>
                        <div className="col-sm-8">
                            <label style={{display: "flex", alignItems: "center"}}>
                                <input
                                    type="radio"
                                    name="diagnostic_point"
                                    value="Rectum"
                                    checked={formData.diagnostic_point === 'Rectum'}
                                    onChange={handleInputChange}
                                />
                                <span style={{fontSize: 15, paddingLeft: 10}}>{t('Rectum')}</span>
                            </label>
                            <label style={{display: "flex", alignItems: "center"}}>
                                <input
                                    type="radio"
                                    name="diagnostic_point"
                                    value="Brain"
                                    checked={formData.diagnostic_point === 'Brain'}
                                    onChange={handleInputChange}
                                />
                                <span style={{fontSize: 15, paddingLeft: 10}}>{t('Brain')}</span>
                            </label>
                        </div>
                    </div>

                    {/* Input Fields */}
                    {[["T1", "dT1"], ["T2", "dT2"], ["Ta", "dTa"], ["dt", "ddt"], ["T0", "dT0"]].map((fields) => (
                        <div className={"form-group row"}>
                            {/*todo*/}
                            <label className={'col-sm-4 col-form-label'}
                                   htmlFor={fields[0]}>{t(names[fields[0]])}:</label>
                            <input
                                className="col-sm-4 form-control"
                                key={fields[0]}
                                type="number"
                                name={fields[0]}
                                value={formData[fields[0]]}
                                onChange={handleInputChange}
                            />
                            <label className={'col-sm-1 col-form-label'} htmlFor={fields[1]}>+-</label>
                            <input
                                className="col-sm-3 form-control"
                                key={fields[1]}
                                type="number"
                                name={fields[1]}
                                value={formData[fields[1]]}
                                onChange={handleInputChange}
                            />
                        </div>))}


                    <button onClick={handleSubmit} className="btn btn-secondary mb-2">
                        {t('Calc')}
                    </button>

                    <br/>

                    {errors.length > 0 && (
                        <div>
                            {errors.map((error, index) => (
                                <div key={index} className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            ))}
                        </div>
                    )}

                    {result && (
                        <div className="alert alert-success" role="alert">
                            <h2 className="text-white">{t('Results')}</h2>
                            <div className="form-group row">
                                <label className="col-sm-6 col-form-label">k:</label>
                                <label className="col-sm-6 col-form-label">{result.k}</label>
                            </div>
                            <div className="form-group row">
                                <label className="col-sm-6 col-form-label">p:</label>
                                <label className="col-sm-6 col-form-label">{result.p}</label>
                            </div>
                            <div className="form-group row">
                                <label className="col-sm-6 col-form-label">{t('TimeDeath')}:</label>
                                <label className="col-sm-6 col-form-label">{result.t}</label>
                            </div>
                            <div className="form-group row">
                                <label className="col-sm-6 col-form-label">{t('MinTimeDeath')}:</label>
                                <label className="col-sm-6 col-form-label">{result.tMin}</label>
                            </div>
                            <div className="form-group row">
                                <label className="col-sm-6 col-form-label">{t('MaxTimeDeath')}:</label>
                                <label className="col-sm-6 col-form-label">{result.tMax}</label>
                            </div>
                            <Plot
                                data={coolingData}
                                layout={{
                                    title: {
                                        text: t("CoolingCurve"),
                                        font: {color: '#2A3F5F'}, // Цвет текста заголовка
                                    },
                                    xaxis: {
                                        title: {
                                            text: t("TimeDeath"),
                                            font: {color: '#2A3F5F'}, // Цвет текста по оси X
                                        },
                                        gridcolor: 'white', // Цвет сетки по оси X
                                        linecolor: 'rgba(0,0,0,0)',
                                    },
                                    yaxis: {
                                        title: {
                                            text: t("Temperature"),
                                            font: {color: '#2A3F5F'}, // Цвет текста по оси Y
                                        },
                                        gridcolor: 'white', // Цвет сетки по оси Y
                                        linecolor: 'rgba(0,0,0,0)',
                                    },
                                    plot_bgcolor: '#E5ECF6', // Цвет фона графика
                                    paper_bgcolor: '#FFFFFF', // Цвет фона всей области графика
                                    gridcolor: 'white', // Цвет сетки на фоне графика
                                    linecolor: 'rgba(0,0,0,0)',
                                }}
                                style={{width: "100%", height: "100%"}} // Ширина и высота 100% родителя
                                useResizeHandler={true}  // Позволяет изменять размер при ресайзе окна
                            />
                            <h4 className="text-white">Below is the function to be minimized, equal to:</h4>
                            <BlockMath math={String.raw`
                            f(k, t) = 
                            \frac{\left(\frac{T1 - Ta}{T0 - Ta} - \frac{p}{p-k}e^{-kt} + \frac{k}{p-k}e^{-pt}\right)^{2} + 
                            \left(\frac{T2 - Ta}{T0 - Ta} - \frac{p}{p-k}e^{-k(t+\Delta t)} + \frac{k}{p-k}e^{-p(t + \Delta t)}\right)^{2}}{2}
                        `}/>
                            <Plot
                                data={[
                                    {
                                        type: 'surface',
                                        x: data.x,
                                        y: data.y,
                                        z: data.z,
                                    },
                                    {
                                        type: 'scatter3d',
                                        mode: 'markers',
                                        x: points.map((i) => i[0]),
                                        y: points.map((i) => i[1]),
                                        z: points.map((i) => 0.5 * (i[2] ** 2 + i[3] ** 2)),
                                        marker: {
                                            size: 5,           // Размер точек
                                            color: 'red',      // Цвет точек
                                            opacity: 0.8,      // Прозрачность точек
                                            symbol: 'circle',  // Форма точек
                                        },
                                        name: t("Iterations"),  // Название набора точек
                                    },
                                ]}
                                layout={{
                                    title: t('MinFunc'),
                                    font: {color: '#2A3F5F'},
                                    scene: {
                                        xaxis: {
                                            title: 't',
                                            //gridcolor: 'white',
                                            //linecolor: 'rgba(0,0,0,0)',
                                            //zerolinecolor: 'rgba(0,0,0,0)',
                                        },
                                        yaxis: {
                                            title: 'k',
                                            //gridcolor: 'white',
                                            //linecolor: 'rgba(0,0,0,0)',
                                            //zerolinecolor: 'rgba(0,0,0,0)',
                                        },
                                        zaxis: {
                                            title: 'f(k, t)',
                                            //gridcolor: 'white',
                                            //linecolor: 'rgba(0,0,0,0)',
                                            //zerolinecolor: 'rgba(0,0,0,0)',
                                        },
                                        //bgcolor: '#E5ECF6', // Цвет фона области графика
                                    },
                                    height: 800,
                                    //plot_bgcolor: '#E5ECF6', // Цвет фона графика
                                    //paper_bgcolor: '#E5ECF6', // Цвет фона всей области графика
                                    linecolor: 'rgba(0,0,0,0)',
                                }}
                                style={{width: "100%", height: "100%"}} // Ширина и высота 100% родителя
                                useResizeHandler={true}  // Позволяет изменять размер при ресайзе окна
                            />
                            <Plot
                                data={[
                                    {
                                        ...itersData,
                                        type: "scatter3d",
                                        mode: 'lines+markers',  // Добавляем линии и точки
                                        marker: {
                                            size: 5,           // Размер точек
                                            opacity: 0.8,      // Прозрачность точек
                                            symbol: 'circle',  // Форма точек
                                        },
                                        line: {
                                            color: 'blue',     // Цвет линии
                                            width: 2,          // Толщина линии
                                        },
                                        name: t("Iterations"),
                                    },
                                ]}
                                layout={{
                                    title: t("IterationsProcess"),
                                    font: {color: '#2A3F5F'},
                                    scene: {
                                        xaxis: {
                                            title: "t",
                                            //gridcolor: 'white',
                                            //linecolor: 'rgba(0,0,0,0)',
                                            //zerolinecolor: 'rgba(0,0,0,0)',
                                        },
                                        yaxis: {
                                            title: "k",
                                            //gridcolor: 'white',
                                            //linecolor: 'rgba(0,0,0,0)',
                                            //zerolinecolor: 'rgba(0,0,0,0)',
                                        },
                                        zaxis: {
                                            title: "f(k, t)",
                                            //gridcolor: 'white',
                                            //linecolor: 'rgba(0,0,0,0)',
                                            //zerolinecolor: 'rgba(0,0,0,0)',
                                        },
                                        //bgcolor: '#E5ECF6', // Цвет фона области графика
                                    },
                                    //plot_bgcolor: '#FFFFFF', // Цвет фона графика
                                    //paper_bgcolor: '#FFFFFF', // Цвет фона всей области графика
                                }}
                                style={{width: "100%", height: "100%"}} // Ширина и высота 100% родителя
                                useResizeHandler={true}  // Позволяет изменять размер при ресайзе окна
                            />
                        </div>
                    )}
                </div>
            </div>
            {!result && (<div className="section section-components pt-150"></div>)}
            <Container fluid style={{backgroundColor: '#f4f5f7'}}>
                <Footer/>
            </Container>
        </div>
    );
};

export default Zcalc1;
